import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/apiClient";
import { useAuth } from "./AuthContext";

const MyListContext = createContext(null);

export const useMyList = () => useContext(MyListContext);

export const MyListProvider = ({ children }) => {
  const { user } = useAuth();
  const uid = user?.id || null;
  const [ids, setIds] = useState(new Set());
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(() => {
    if (!uid) {
      setIds(new Set());
      setLoaded(true);
      return;
    }
    api
      .get("/mylist")
      .then((r) => setIds(new Set(r.data.ids || [])))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [uid]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isSaved = useCallback((id) => ids.has(id), [ids]);

  const toggle = useCallback(
    async (id) => {
      if (!user) return null;
      const wasSaved = ids.has(id);
      setIds((prev) => {
        const next = new Set(prev);
        if (wasSaved) next.delete(id);
        else next.add(id);
        return next;
      });
      try {
        if (wasSaved) await api.delete(`/mylist/${id}`);
        else await api.post("/mylist", { video_id: id });
      } catch (e) {
        setIds((prev) => {
          const next = new Set(prev);
          if (wasSaved) next.add(id);
          else next.delete(id);
          return next;
        });
        return wasSaved;
      }
      return !wasSaved;
    },
    [ids, user]
  );

  const clearAll = useCallback(async () => {
    if (!user) return;
    const prev = ids;
    setIds(new Set());
    try {
      await api.delete("/mylist");
    } catch (e) {
      setIds(prev);
    }
  }, [ids, user]);

  return (
    <MyListContext.Provider value={{ ids, isSaved, toggle, clearAll, refresh, loaded }}>
      {children}
    </MyListContext.Provider>
  );
};
